import { SubscriptionsContext } from "@/context/SubscriptionsContext";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";

// This interface must stay in sync with messaging app's SubscriptionsProvider.
// Both apps call the same GET /Subscription API endpoint.
export interface Subscription {
  id: number;
  siteId: number;
  // Channels
  enableWeb: boolean;
  enableSms: boolean;
  enableEmail: boolean;
  enableWhatsapp: boolean;
  enableFacebook: boolean;
  enableInstagram?: boolean;
  enableTikTok?: boolean;
  enableIvr: boolean;
  // Core Features
  enableNetsuite: boolean;
  enableAutomation: boolean;
  enableAI: boolean;
  enableWorkflowRules: boolean;
  enableFormstack: boolean;
  enableContactManager: boolean;
  enableKnowledgeBase: boolean;
  enableInvitations: boolean;
  enableSemanticSearch: boolean;
  enableAdvancedAIFeatures: boolean;
  /** "basic" | "pro" | "enterprise" — derived server-side from DefaultAiModel, not stored. */
  aiModelTier?: string;
  /** Site-level default AI model alias set by superadmin. Null = platform default. */
  defaultAiModel?: string;
  /** True when Velaro has configured a custom API key for this site. Key itself is never exposed. */
  aiApiKeyConfigured?: boolean;
  /** Custom Azure OpenAI endpoint if set. */
  aiEndpointOverride?: string;
  enableAccela: boolean;
  enableShopify: boolean;
  // Shopify skill packages
  enableShopifyCore?: boolean;
  enableShopifyCommerce?: boolean;
  enableShopifyBtoBOps?: boolean;
  enableShopifyLogistics?: boolean;
  enableWooCommerce?: boolean;
  // WooCommerce skill packages
  enableWooCommerceCore?: boolean;
  enableWooCommerceCommerce?: boolean;
  enableWooCommerceBtoBOps?: boolean;
  enableWooCommerceLogistics?: boolean;
  enableMultipleDeployments: boolean;
  maxDeployments?: number;
  enableTicketing?: boolean;
  enableCoBrowse?: boolean;
  enableClickToCall?: boolean;
  maxIndexCount: number;
  // Reports
  enableConversationReports?: boolean;
  enableMissedChatReports?: boolean;
  enableSentimentReports?: boolean;
  enableAgentPerformanceReports?: boolean;
  enableResponseTimeReports?: boolean;
  enableCallReports?: boolean;
  enableAccountUtilizationReports?: boolean;
  enableAgentAvailabilityReports?: boolean;
  enableAgentUtilizationReports?: boolean;
  enableAuditReporting?: boolean;
  enableIvrReports?: boolean;
  enableCustomThresholds?: boolean;
  enableAgentDashboard?: boolean;
  enableScheduleAdherenceReports?: boolean;
  enableCustomDashboards?: boolean;
  maxCustomDashboards?: number;
  enableReportEmailSubscriptions?: boolean;
  enableTicketReports?: boolean;
  // Report Limits
  maxExportDays?: number;
  asyncResponseThresholdMinutes?: number | null;
  maxReportExportsPerMonth?: number;
  maxReportEmailsPerMonth?: number;
  maxScheduledReports?: number;
  // Channel Limits
  maxSmsPerMonth?: number | null;
  maxIvrMinutesPerMonth?: number | null;
  // Security
  enableEnterpriseSso?: boolean;
  enablePasskeys?: boolean;
  enableAdvancedMfa?: boolean;
  maxSsoProviders?: number;
  enableIpBlocking?: boolean;
  maxBlockedIps?: number;
  // Custom Statuses
  enableCustomStatuses?: boolean;
  maxCustomStatuses?: number;
  // Message Scrubbing
  enableMessageScrubbing?: boolean;
  // Agent Idle Detection
  enableAutoIdle?: boolean;
  autoIdleMinutes?: number;
  autoIdleStatus?: string;
  allowAgentIdleOverride?: boolean;
  // Usage & Limits
  maxLicensedUsers?: number;
  maxCreatedUsers?: number;
  licenseCooldownHours?: number;
  inboxRetentionDays?: number;
  activityLogRetentionDays?: number;
  maxConversationsPerMonth?: number;
  maxCustomFields?: number;
  maxContacts?: number;
  maxOutboundEmailsPerMonth?: number;
  enableUsageAlerts?: boolean;
  usageWarningThreshold?: number;
  usageCriticalThreshold?: number;
  overageBehavior?: string;
  // SMS Alerts
  enableSmsAlerts?: boolean;
  maxSmsAlertsPerMonth?: number;
  // Routing
  enableAutoRouting?: boolean;
  // Conversation Shadowing
  enableConversationShadowing?: boolean;
  // Video Chat
  enableVideoChat?: boolean;
  // ElevenLabs Bot Voice — TTS for AI chatbot replies
  enableBotVoice?: boolean;
  // Monthly character quota for bot chat TTS (0 = unlimited). Separate from IVR TTS.
  maxAvatarVoiceCharsPerMonth?: number;
  // Tier Escalation — structured Tier 1/2/3 routing per team with ring timeouts
  enableTierEscalation?: boolean;
  // Integrations — subscription-gated (mirrors Subscription.cs Enable* bool properties)
  // Add new integration flags here AND in integrationGateMap in IntegrationsLanding.tsx.
  // Run scripts/sync-subscription-flags.js to check sync with Subscription.cs.
  enableHubSpot?: boolean;
  enableBigCommerce?: boolean;
  // BigCommerce skill packages
  enableBigCommerceCore?: boolean;
  enableBigCommerceCommerce?: boolean;
  enableBigCommerceBtoBOps?: boolean;
  enableBigCommerceLogistics?: boolean;
  enableMondaySkills?: boolean;
  enableDynamics365?: boolean;
  enableMagento?: boolean;
  // Magento skill packages
  enableMagentoCore?: boolean;
  enableMagentoCommerce?: boolean;
  enableMagentoBtoBOps?: boolean;
  enableMagentoLogistics?: boolean;
  enableSalesforce?: boolean;
  enableKlaviyo?: boolean;
  enableServiceNow?: boolean;
  enableSapSkills?: boolean;
  enableSquare?: boolean;
  enableSquareRetail?: boolean;
  enableZapier?: boolean;
  enablePowerAutomate?: boolean;
  enableMake?: boolean;
  enableInChatPayments?: boolean;
  enableNetSuiteSkills?: boolean;
  enableHubSpotSkills?: boolean;
  enablePipedriveSkills?: boolean;
  enableQuickBooksSkills?: boolean;
  enableZohoSkills?: boolean;
  enableToastSkills?: boolean;
  enableCloverSkills?: boolean;
  enableMindbodySkills?: boolean;
  enableJobberSkills?: boolean;
  enableGuestySkills?: boolean;
  enableUplistingSkills?: boolean;
  enableCloudbedsSkills?: boolean;
  enableNativeCrm?: boolean;
  enableMcp?: boolean;
  enableMcpApi?: boolean;
  enableCopilotWrite?: boolean;
  enableOrchestration?: boolean;
  // Channels
  enableAppleMessages?: boolean;
  // Ticketing add-ons
  enableTicketSla?: boolean;
  enableTicketAutomation?: boolean;
  enableTicketCsat?: boolean;
  enableTicketCoaching?: boolean;
  enableTicketMerge?: boolean;
  // Email
  enableEmailCampaigns?: boolean;
  enableGmailIntegration?: boolean;
  enableEmailAiResolution?: boolean;
  // Agent features
  enableCollisionDetection?: boolean;
  enableContactTimeline?: boolean;
  enableAgentSignatures?: boolean;
  enableAgentLeaderboard?: boolean;
  enableAgentBotObservation?: boolean;
  enableChatTranslations?: boolean;
  enableAgentToAgent?: boolean;
  enableAgentAssist?: boolean;
  // Workflow / AI
  enableWorkflowAi?: boolean;
  enableCommerceSkills?: boolean;
  enableOpsSkills?: boolean;
  enableProductCatalog?: boolean;
  enableQuoteGeneration?: boolean;
  enableAppointmentScheduling?: boolean;
  enableConversationTraining?: boolean;
  enablePostChatCrmSummary?: boolean;
  enableQuoteMaker?: boolean;
  // Recruiting Bot
  enableRecruitingBot?: boolean;
  enableRecruitingBotEeoc?: boolean;
  // CRM / contacts
  enableCustomerHealthScore?: boolean;
  enableHealthScoreReports?: boolean;
  enableBuyingSignals?: boolean;
  // KB / Indexing — numeric limits read from DB, never hardcoded in UI
  enableScraperBotIndexing?: boolean;
  enableYoutubeBotIndexing?: boolean;
  enableBigCommerceBotIndexing?: boolean;
  enableShopifyBotIndexing?: boolean;
  enableScraperAutoRebuild?: boolean;
  scraperRebuildIntervalDays?: number;
  maxProductsIndexed?: number;       // 0 = unlimited (Large Catalog add-on)
  maxScraperPagesPerMonth?: number;
  maxKbArticles?: number;
  kbAutoReindexIntervalHours?: number;
  enableKbBotIndexing?: boolean;
  enableFileIngestion?: boolean;
  enableOneDriveIngestionSync?: boolean;
  // Operations
  enableSoftphone?: boolean;
  enablePortalCustomDomain?: boolean;
  enableAvatarMode?: boolean;
  enableWorkforceManagement?: boolean;
  enableOpsIntelligence?: boolean;
  enableSurveyReports?: boolean;
  enableServiceLevelReports?: boolean;
  enableSelfServeSeats?: boolean;
  enablePriorityQueue?: boolean;
  enablePokerRoomIvr?: boolean;
  enableIvrBridgeCall?: boolean;
  // Compliance — BYOS transcript archiving to Azure Blob / AWS S3
  enableTranscriptStorage?: boolean;
  // Data Retention & GDPR
  enableDataRetention?: boolean;
  enableScheduledPurge?: boolean;
  minPurgeIntervalDays?: number;
  // Agent Login Security
  enableAgentIpRestriction?: boolean;
  enableSessionTimeout?: boolean;
  sessionTimeoutMinutes?: number;
  enableConcurrentSessionLimit?: boolean;
  maxConcurrentSessions?: number;
  // REST API Access
  maxApiKeys?: number;
  apiRateLimitPerMinute?: number;
  apiDailyLimit?: number;
  // Click-to-Call
  enableClickToCallWidget?: boolean;
  enableByokTwilioCtc?: boolean;
  enableCtcAiPrescreen?: boolean;
  enableCtcSmartScheduling?: boolean;
  enableCtcCallRecording?: boolean;
  enableCtcGeographicRouting?: boolean;
  enableClickToCallReports?: boolean;
  clickToCallMinutesPerMonth?: number;
  // Conversation Quality & Agent Intelligence
  enableConversationQuality?: boolean;
  enableAgentSmartReply?: boolean;
  enableChatIntelligenceReport?: boolean;
  // Bot Communications (IVR/bot outbound SMS and email)
  enableBotSmsSkill?: boolean;
  enableBotEmailSkill?: boolean;
  maxBotSmsPerHour?: number;
  maxBotEmailsPerHour?: number;
  enableAiComposedEmails?: boolean;
  maxAiComposedEmailsPerDay?: number;
  // Post Conversation compliance features
  enableComplianceMode?: boolean;
  enablePiiRedaction?: boolean;
  // RCS (Rich Communication Services)
  enableRcs?: boolean;
  enableRcsRichMessages?: boolean;
  rcsSmsFallbackEnabled?: boolean;
  maxRcsPerMonth?: number;
  // AI Skill Tier Gating
  enableAdvancedAiSkills?: boolean;    // Write-tier: create/update records, assign tasks, send emails
  enableDestructiveAiSkills?: boolean; // Destructive-tier: cancel orders, issue refunds, delete records
  // Visitor Tracking
  enableVisitorTracking?: boolean;
  // Microsoft Bookings AI skills (requires M365 connection)
  enableMicrosoftBookingsSkills?: boolean;
  // VAST Retail (Kerridge CS) tire shop DMS AI skills
  enableVastSkills?: boolean;
  // Nextiva VoIP CTI — screen pop, call logging
  enableNextiva?: boolean;
  // AI model selection — lets customers pick their own model in bot settings.
  // Enterprise only by default; enabling on lower tiers risks unexpected cost increases.
  enableCustomAiModel?: boolean;
  // Theme & Intelligence
  enableThemeClassification?: boolean;
  enableConversationLifecycle?: boolean;
  enableIntelligenceSuite?: boolean;
  enableReportNarratives?: boolean;
  // E-commerce sync
  enableEcommerceSync?: boolean;
  // Integrations
  enableOutlookIntegration?: boolean;
  // IVR diagnostics panel
  enableIvrDiagnostics?: boolean;
  // Branding — false = white-label plan (can remove "Powered by Velaro" from widget + emails)
  showBranding?: boolean;
}

export function SubscriptionsProvider(props: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  const fetch = useCallback(async () => {
    try {
      const response = await api.messaging.get("Subscription");
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch {
      // keep subscription null — consumers use safe defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <SubscriptionsContext.Provider value={{ subscription, loading }}>
      {props.children}
    </SubscriptionsContext.Provider>
  );
}
